import React from "react";
import { GetServerSideProps } from "next";

import useTranslation from "next-translate/useTranslation";
import { useTheme } from "emotion-theming";

import api, { cacheAsyncFn } from "api/api";
import LocalizedLayout from "@components/LocalizedLayout";

import { withI18n, importNamespaces } from "../../../src/utils/i18n";
import { Theme } from "@utils/styled";
import { css } from "@emotion/core";

const pageComponents = ["SingleMix"];

const Page = ({ headerData, components, footerData }) => {
  const theme = useTheme<Theme>();
  const { lang } = useTranslation();

  let dynamicComponents = [] as any;
  if (components)
    dynamicComponents = pageComponents.map((component) => {
      const c = require(`../../../src/components/${component}`).default;
      const componentData =
        components.find((c) => c.hasOwnProperty(component))[component] || null;

      const { data, errorCode } = componentData;

      return {
        Component: c,
        data,
        errorCode,
      };
    });

  return (
    <div
      css={css`
        background: ${theme.palette.background};
        color: ${theme.palette.text.primary};
        width: 100%;
        transition-duration: 0.2s;
        transition-property: background-color, color;
      `}
    >
      <LocalizedLayout
        lang={lang}
        headerData={headerData}
        footerData={footerData}
      >
        {dynamicComponents.map(({ Component, data, errorCode }, id) => {
          return (
            <Component key={id} data={data} errorCode={errorCode} lang={lang} />
          );
        })}
      </LocalizedLayout>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<
  {},
  { lang: string; slug: string }
> = async (context) => {
  //@ts-ignore  Context is always defined
  const { lang, slug: _slug } = context.params;

  let slug = _slug || "home";

  const menus = await cacheAsyncFn(api.getMenus, lang, "header");
  const footer = await cacheAsyncFn(api.getMenus, lang, "footer");
  const socials = await cacheAsyncFn(api.getMenuSocials, lang);

  const stripTrailingSlash = (str) => {
    return str.endsWith("/") ? str.slice(0, -1) : str;
  };

  const headerData = menus?.menu_items?.map((page) => ({
    href: page.href,
    title: page.title,
    as: stripTrailingSlash(`/${lang}${page.href}`),
  }));

  const footerData = footer?.menu_items?.map((page) => ({
    href: page.href,
    title: page.title,
    as: stripTrailingSlash(`/${lang}${page.href}`),
  }));

  const components = await Promise.all(
    pageComponents.map(async (component) => {
      const c = require(`../../../src/components/${component}`).default;

      const componentData = await c.getData(lang, slug);

      return {
        [component]: {
          data: componentData.data,
          errorCode: componentData.errorCode || 0,
        },
      };
    })
  );

  return {
    props: {
      lang,
      slug,
      namespaces: await importNamespaces(lang, ["common"] as any),
      headerData,
      footerData: [footerData, socials],
      components,
    },
  };
};

export default withI18n(Page);
